"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import type { EFormPinInputProps } from "./formPINInput.types";

// ------------------------------- CONSTANTS -------------------------------

const PIN_INPUT_SIZE_CONFIG = {
	default: {
		gapClassName: "gap-2",
		inputClassName: "p-3 text-2xl",
		width: 44,
	},
	compact: {
		gapClassName: "gap-1.5",
		inputClassName: "py-2 text-xl",
		width: 40,
	},
} as const;

// -------------------------------- HELPERS --------------------------------

function onlyDigits(input: string) {
	return input.replace(/\D/g, "");
}

// ------------------------------- COMPONENT -------------------------------

export default function FormPinInput(props: Readonly<EFormPinInputProps>) {
	const {
		length = 6,
		size = "default",
		value,
		defaultValue,
		onChange,
		onComplete,
		autoFocus,
		autoComplete,
		focusLastInput,
		masked = false,
		disabled,
		error,
		helperText,
		separator,
		className,
		inputClassName,
		name,
		...rest
	} = props;
	const sizeConfig = PIN_INPUT_SIZE_CONFIG[size];

	const isControlled = value != null;
	const [internalValue, setInternalValue] = useState(() => onlyDigits(defaultValue ?? "").slice(0, length));
	const [revealed, setRevealed] = useState(false);
	const toggleReveal = useCallback(() => setRevealed((valorAtual) => !valorAtual), []);

	const currentValue = isControlled ? (value ?? "") : internalValue;
	const normalizedValue = onlyDigits(currentValue).slice(0, length);
	const chars = normalizedValue.split("");
	const instanceId = useId();

	const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

	const inputKeys = Array.from({ length }, (_, idx) => `${instanceId}-${idx}`);

	useEffect(() => {
		if (!autoFocus) return;
		const idx = Math.min(normalizedValue.length, length - 1);
		inputsRef.current[idx]?.focus();
		inputsRef.current[idx]?.select();
	}, [autoFocus, length, normalizedValue.length]);

	useEffect(() => {
		if (!focusLastInput || disabled) return;
		const lastInput = inputsRef.current[length - 1];
		lastInput?.focus();
		lastInput?.select();
	}, [disabled, focusLastInput, length]);

	useEffect(() => {
		if (!onComplete) return;
		if (normalizedValue.length !== length) return;
		onComplete(normalizedValue);
	}, [length, normalizedValue, onComplete]);

	useEffect(() => {
		if (!masked || disabled) return;

		const handleRevealShortcut = (ev: KeyboardEvent) => {
			if (ev.key !== "F8" || ev.repeat) return;
			ev.preventDefault();
			toggleReveal();
		};

		globalThis.addEventListener("keydown", handleRevealShortcut);
		return () => globalThis.removeEventListener("keydown", handleRevealShortcut);
	}, [disabled, masked, toggleReveal]);

	function commit(next: string) {
		const nextValue = onlyDigits(next).slice(0, length);
		if (!isControlled) setInternalValue(nextValue);
		onChange?.(nextValue);
	}

	function focusIndex(index: number) {
		const clamped = Math.max(0, Math.min(length - 1, index));
		inputsRef.current[clamped]?.focus();
		inputsRef.current[clamped]?.select();
	}

	function handleChange(index: number, raw: string) {
		const incoming = onlyDigits(raw);
		if (!incoming) {
			const nextChars = [...chars];
			nextChars[index] = "";
			commit(nextChars.join(""));
			return;
		}

		// Se o usuário colou múltiplos dígitos num único campo, distribuímos a partir do índice atual.
		const nextChars = [...Array.from({ length }, (_, i) => chars[i] ?? "")];
		for (let offset = 0; offset < incoming.length && index + offset < length; offset++)
			nextChars[index + offset] = incoming[offset];

		commit(nextChars.join(""));
		focusIndex(Math.min(index + incoming.length, length - 1));
	}

	function handleKeyDown(index: number, ev: React.KeyboardEvent<HTMLInputElement>) {
		if (ev.key === "Backspace") {
			// Se já tem valor neste índice, apenas limpa; senão, volta um campo.
			if (chars[index]) {
				const nextChars = [...chars];
				nextChars[index] = "";
				commit(nextChars.join(""));
				return;
			}
			ev.preventDefault();
			focusIndex(index - 1);
			return;
		}

		if (ev.key === "ArrowLeft") {
			ev.preventDefault();
			focusIndex(index - 1);
			return;
		}

		if (ev.key === "ArrowRight") {
			ev.preventDefault();
			focusIndex(index + 1);
			return;
		}

		if (ev.key === " " || ev.key === "Spacebar") ev.preventDefault();
	}

	function handlePaste(index: number, ev: React.ClipboardEvent<HTMLInputElement>) {
		const pasted = onlyDigits(ev.clipboardData.getData("text"));
		if (!pasted) return;
		ev.preventDefault();
		handleChange(index, pasted);
	}

	return (
		<FormControl error={error} disabled={disabled} className={className}>
			<Box className="flex flex-col items-center">
				<Box className={clsx("flex items-center justify-center", sizeConfig.gapClassName)}>
					{Array.from({ length }).map((_, index) => (
						<TextField
							key={inputKeys[index]}
							autoFocus={autoFocus && index === 0 && !disabled}
							size="small"
							value={chars[index] ?? ""}
							onChange={(ev) => handleChange(index, ev.target.value)}
							inputRef={(el) => {
								inputsRef.current[index] = el;
							}}
							disabled={disabled}
							slotProps={{
								htmlInput: {
									type: masked && !revealed ? "password" : "text",
									autoComplete,
									inputMode: "numeric",
									pattern: "[0-9]*",
									maxLength: 1,
									name: name ? `${name}-${index}` : undefined,
									"aria-label": rest["aria-label"] ? `${rest["aria-label"]} ${index + 1}` : undefined,
									className: clsx(
										"text-center font-semibold",
										sizeConfig.inputClassName,
										inputClassName,
									),
									onKeyDown: (ev) => handleKeyDown(index, ev),
									onPaste: (ev) => handlePaste(index, ev),
								},
							}}
							sx={{
								width: sizeConfig.width,
								"& input": { paddingX: 0 },
							}}
						/>
					))}
					{separator ? <span className="opacity-70 select-none">{separator}</span> : null}
				</Box>

				{masked ? (
					<span className="mt-1 inline-flex">
						<Button
							type="button"
							variant="text"
							size="small"
							disabled={disabled}
							aria-pressed={revealed}
							aria-keyshortcuts="F8"
							onClick={toggleReveal}
							startIcon={
								<FuseSvgIcon size={18}>{revealed ? "lucide:eye-off" : "lucide:eye"}</FuseSvgIcon>
							}
						>
							{revealed ? "Ocultar código" : "Exibir código"}
						</Button>
					</span>
				) : null}
			</Box>

			{helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
		</FormControl>
	);
}
